import { createContext, useContext, useEffect, useMemo, useState } from "react";

const BasketContext = createContext(null);
const STORAGE_KEY = "ejf_basket_v1";

const itemKey = (it) => `${it.product_id}|${it.variation || ""}|${(it.addons || []).map((a) => a.name).sort().join(",")}`;

export function BasketProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, { variation = null, addons = [], quantity = 1 } = {}) => {
    const unit = variation ? Number(variation.price) : Number(product.sale_price || product.price);
    const entry = {
      product_id: product.id,
      slug: product.slug,
      name: product.name,
      image: (product.images || [])[0] || "",
      unit_price: unit,
      variation: variation ? variation.name : null,
      addons: addons.map((a) => ({ name: a.name, price: Number(a.price) })),
      quantity,
    };
    setItems((prev) => {
      const key = itemKey(entry);
      const idx = prev.findIndex((p) => itemKey(p) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, entry];
    });
  };

  const updateQuantity = (key, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((p) => itemKey(p) !== key)
        : prev.map((p) => (itemKey(p) === key ? { ...p, quantity } : p))
    );
  };

  const remove = (key) => setItems((prev) => prev.filter((p) => itemKey(p) !== key));
  const clear = () => setItems([]);

  const { count, subtotal } = useMemo(() => {
    let count = 0;
    let subtotal = 0;
    for (const it of items) {
      count += it.quantity;
      subtotal += (it.unit_price + (it.addons || []).reduce((s, a) => s + a.price, 0)) * it.quantity;
    }
    return { count, subtotal: Math.round(subtotal * 100) / 100 };
  }, [items]);

  return (
    <BasketContext.Provider value={{ items, add, updateQuantity, remove, clear, count, subtotal, itemKey }}>
      {children}
    </BasketContext.Provider>
  );
}

export const useBasket = () => useContext(BasketContext);
